import {
  type Consumer, createConsumer, type Mixin, type Subscription,
} from '@rails/actioncable';
import {
  createContext, type PropsWithChildren,
  useCallback,
  useContext, useEffect,
  useMemo, useRef, useState,
} from 'react';

interface ActionCableContextType {
  subscribe: (
    channelName: string,
    channelParams: Record<string, unknown>,
    callbacks: Mixin
  ) => { subscription: Subscription | null; subscriptionId: string };
  unsubscribe: (subscriptionId: string) => void;
  cable: Consumer | null;
  subscriptions: Record<string, Subscription>;
}

interface ActionCableProviderProps {
  url: string;
}

const ActionCableContext = createContext<ActionCableContextType | null>(null);

export function ActionCableProvider({
  children,
  url,
}: PropsWithChildren<ActionCableProviderProps>) {
  const cableRef = useRef<Consumer | null>(null);
  const subscriptionsRef = useRef<Record<string, Subscription>>({});
  const subscriptionCounterRef = useRef(0);
  const [subscriptionsVersion, setSubscriptionsVersion] = useState(0);

  const generateSubscriptionId = (channelName: string, channelParams: Record<string, unknown>): string => {
    subscriptionCounterRef.current += 1;
    const paramsHash = JSON.stringify(channelParams);
    return `${channelName}_${paramsHash}_${subscriptionCounterRef.current}`;
  };

  const initCable = useCallback(() => {
    const consumer = createConsumer(url);
    cableRef.current = consumer;
    return consumer;
  }, [url]);

  const disconnect = (): void => {
    Object.values(subscriptionsRef.current).forEach((sub: Subscription) => sub.unsubscribe());
    subscriptionsRef.current = {};
    setSubscriptionsVersion((v) => v + 1);
    if (cableRef.current) cableRef.current.disconnect();
  };

  useEffect(() => {
    let consumer: Consumer | null = null;
    if (!cableRef.current) consumer = initCable();

    return () => {
      if (consumer || cableRef.current) disconnect();
    };
  }, [initCable, url]);

  const subscribe = useCallback((
    channelName: string,
    channelParams: Record<string, unknown>,
    callbacks: Mixin,
  ): { subscription: Subscription | null; subscriptionId: string } => {
    let consumer = cableRef.current;
    if (!consumer?.connection) {
      consumer = initCable();
    }

    const subscription = consumer.subscriptions.create(
      { channel: channelName, ...channelParams },
      callbacks,
    );

    const subscriptionId = generateSubscriptionId(channelName, channelParams);
    subscriptionsRef.current[subscriptionId] = subscription;
    setSubscriptionsVersion((v) => v + 1);
    return { subscription, subscriptionId };
  }, [initCable]);

  const unsubscribe = useCallback((subscriptionId: string): void => {
    if (subscriptionsRef.current[subscriptionId]) {
      subscriptionsRef.current[subscriptionId].unsubscribe();
      delete subscriptionsRef.current[subscriptionId];
      setSubscriptionsVersion((v) => v + 1);
    }
  }, []);

  const contextValue = useMemo(() => ({
    subscribe,
    unsubscribe,
    cable: cableRef.current,
    subscriptions: subscriptionsRef.current,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [subscribe, unsubscribe, subscriptionsVersion]);

  return (
    <ActionCableContext.Provider value={contextValue}>
      {children}
    </ActionCableContext.Provider>
  );
}

export const useActionCable = (): ActionCableContextType => {
  const context = useContext(ActionCableContext);
  if (!context) {
    throw new Error('useActionCable must be used within an ActionCableProvider');
  }
  return context;
};
